/**
 * The live queue.
 *
 * Five columns on the desk, where there is room for what the automation is doing and who
 * has it; four in the map view's narrow column, where there is not. The same rows either
 * way — a dispatcher who switches views is looking at the same queue, not a different one.
 */

import { useTranslation } from 'react-i18next';

import { useDispatch } from '../shell/dispatchContext';
import {
  Button,
  Card,
  CardHeader,
  Count,
  EmptyState,
  Pill,
  Sub,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import { timeSpanToMinutes, type QueuedOrder } from './api';
import { stateTone, waitingLook } from './states';

export function QueueCard({
  orders,
  loading,
  selectedId,
  onOpen,
  compact,
  className,
}: {
  orders: QueuedOrder[];
  loading: boolean;
  selectedId: string | null;
  onOpen: (orderId: string) => void;
  /** Three columns instead of four, for the map view's narrower column. */
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();

  const needing = orders.filter((order) => order.auto.needsAttention).length;

  return (
    <Card className={className}>
      <CardHeader
        title={t('queue.title')}
        actions={
          needing > 0 ? (
            <span className="text-secondary text-ink-3">
              {t('queue.needing', { count: needing })}
            </span>
          ) : undefined
        }
      >
        <Count>{orders.length}</Count>
      </CardHeader>

      {loading ? (
        <EmptyState>{t('app.loading')}</EmptyState>
      ) : orders.length === 0 ? (
        <EmptyState>{t('queue.empty')}</EmptyState>
      ) : (
        <div className="overflow-auto min-h-0">
          {/* Tight in both shapes: the desk gives the queue 533px for five columns, which
              is the mockup's own arithmetic and only fits with the narrower padding. */}
          {/*
            * Fixed layout, which is the only thing that actually holds this table to the
            * width the desk gives it.
            *
            * An automatic table sizes every column to its content, so one long driver name
            * or a note like "offered to Georgi Yanev, attempt 4" sets a floor under the
            * whole table and the queue grows a horizontal scrollbar that never goes away.
            * Truncating the text inside the cell does not help: a cell still reports the
            * width its content wants, and `min-w-0` is a flex rule, not a table one. Under
            * `table-fixed` the columns are decided by the header alone and the cells clip,
            * which is what makes `truncate` mean anything here.
            *
            * The gutter is halved here and nowhere else. `tight` is shared with the firm
            * list and both shift books, and none of them is fighting for width the way a
            * queue with four columns in 547px is — so this narrows the one table that needs
            * it rather than re-spacing three that do not.
            */}
          <Table tight className="table-fixed [&_th]:px-[5px] [&_td]:px-[5px]">
            <thead>
              <tr>
                {/* Reference and waiting time share a column, stacked, with the take-over
                    button beside them. Three columns became one: the queue was asking for
                    more width than the desk has and paying for it with a horizontal
                    scrollbar that was always on screen, and the two values are short
                    enough to sit one above the other without a row growing.

                    The heading names only the first of the two, with both on hover. A
                    header is a label, not content, and one wide enough to say everything
                    would set a floor under a column whose whole purpose here is to be
                    narrow. */}
                {/* 64px, which is 54 of content after the gutters. Measured rather than
                    guessed: the header is 43, a five-character reference 40, and "120 мин"
                    44. The one string that does not fit is a pre-booking's "след 15 мин" at
                    66, and it is allowed to take a second line — pre-bookings enter the
                    queue fifteen minutes before they are due, so there is rarely one, and
                    the cell wraps rather than clipping. */}
                <Th
                  className="w-16"
                  title={`${t('queue.reference')} · ${t('queue.waiting')}`}
                >
                  {t('queue.reference')}
                </Th>
                <Th>{t('queue.pickup')}</Th>
                {/* Wider than an even share, because the pill inside it does not wrap and
                    Bulgarian is the long language here: "Пътува към клиента" measures
                    128px against the 130px an even quarter would have left it. Two pixels
                    is not headroom, it is a clipped word waiting for a font fallback. */}
                <Th className="w-40">{t('queue.state')}</Th>
                {/* Fixed too, so that the address is the one column left to take whatever
                    the card has spare. Under `table-fixed` every column without a width
                    shares the slack evenly, and on a wide desk that gave the driver — a car
                    number and one line of note — as much room as the address, which carries
                    the pickup and the destination and is the only one that wraps. 144px
                    holds the number and "Търси шофьор" whole; a longer note was already
                    truncated with its full text on a title. */}
                {!compact && <Th className="w-36">{t('queue.driver')}</Th>}
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <QueueRow
                  key={order.orderId}
                  order={order}
                  selected={selectedId === order.orderId}
                  compact={compact}
                  onOpen={() => onOpen(order.orderId)}
                />
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function QueueRow({
  order,
  selected,
  compact,
  onOpen,
}: {
  order: QueuedOrder;
  selected: boolean;
  compact?: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const minutes = timeSpanToMinutes(order.waitingFor);

  // A pre-booking that is not yet due reads as negative waiting time. Showing that as
  // "waiting −480 minutes" would be nonsense; it is due in eight hours.
  const pending = minutes < 0;

  return (
    <Tr
      selected={selected}
      onClick={onOpen}
      // A row is a control, so it is focusable and answers Enter. The common path through
      // this console is meant to be driven without a mouse.
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      {/* `wrap`, because a cell is nowrap unless it asks otherwise and this column is the
          narrowest on the table. A reference has no spaces and stays on its line; the
          waiting time is short until a pre-booking makes it "след 1440 мин", which without
          this is cut off mid-word with no ellipsis to say so — clipped silently, which is
          the one failure a narrow column must not have. */}
      <Td wrap className="align-top">
        <span className="font-mono font-semibold tracking-[0.03em] text-secondary block">
          {order.reference}
        </span>
        <Sub block className={waitingLook(minutes)}>
          {pending
            ? t('queue.dueIn', { minutes: Math.round(-minutes) })
            : t('queue.minutes', { minutes: Math.round(minutes) })}
        </Sub>
      </Td>

      {/* The destination is the second line the design asks for; the caller's name stands
          in where a caller never named one, which is most rides taken over the telephone. */}
      <Td wrap>
        {order.pickupAddress}
        {order.destinationAddress != null ? (
          <Sub block>→ {order.destinationAddress}</Sub>
        ) : (
          order.customerName != null && <Sub block>{order.customerName}</Sub>
        )}
      </Td>

      <Td>
        <Pill tone={stateTone(order.state)}>
          {t(`states.${order.state}`, { defaultValue: order.state })}
        </Pill>
      </Td>

      {!compact && (
        <Td>
          <Driver order={order} />
        </Td>
      )}
    </Tr>
  );
}

/**
 * Who has the order, and what the automation is doing about it.
 */
function Driver({ order }: { order: QueuedOrder }) {
  const { t } = useTranslation();
  const { showDriver } = useDispatch();

  const note = !order.auto.automatic
    ? t('queue.byHand')
    : order.auto.offeredToCount !== null
      ? t('queue.offeredToMany', { count: order.auto.offeredToCount })
      : order.auto.offeredToName !== null
        ? t('queue.offeredTo', {
            name: order.auto.offeredToName,
            attempt: order.auto.attempt ?? 1,
          })
        : t('queue.searching');

  const stage =
    order.auto.stage !== null
      ? ` · ${t(`queue.stage.${order.auto.stage}`, { defaultValue: order.auto.stage })}`
      : '';

  // Both lines truncate, and that is what stops this column setting a floor under the whole
  // table. Its minimum width used to be whichever was longer — the longest driver name, or
  // a note like "offered to Georgi Yanev, attempt 1 · 2" that could not wrap because a cell
  // is `whitespace-nowrap` unless it asks otherwise. Either one put the table over the
  // width the desk has, and the price was a horizontal scrollbar that never went away.
  // Both carry their full text on a title.
  return (
    <div className="min-w-0">
      {order.auto.needsAttention ? (
        <Pill tone="danger" soft title={t('queue.needsAttentionHelp')}>
          {t('queue.needsAttention')}
        </Pill>
      ) : order.driverId === null ? (
        <span className="block text-ink-3">—</span>
      ) : (
        /* The car's short number, as a button that shows it on the map: an office knows its
           cars by number, not by name, and the name is on the title for whoever does not.
           A driver without a number gets their name, so the button is never blank. */
        <Button
          size="sm"
          variant="outline"
          className="font-mono tracking-[0.03em]"
          title={t('queue.showOnMap', { name: order.driverName ?? '' })}
          onClick={(event) => {
            // The row underneath opens the order; this opens the car.
            event.stopPropagation();
            showDriver(order.driverId);
          }}
        >
          {order.driverCallSign ?? order.driverName ?? '—'}
        </Button>
      )}

      <Sub block className="truncate" title={note + stage}>
        {note}
        {stage}
      </Sub>
    </div>
  );
}
